import { useState } from 'react';
import { useExpenses, useSubmitExpense, useDeleteExpense } from '../hooks/useExpenses.js';
import { useJobs } from '../hooks/useJobs.js';
import { useError } from '../components/shared/ErrorBanner.jsx';
import { centsToUsd } from '../lib/format.js';

const KINDS = [
  { id: 'mileage',  label: 'Mileage' },
  { id: 'material', label: 'Material' },
  { id: 'per_diem', label: 'Per diem' },
  { id: 'tools',    label: 'Tools' },
  { id: 'other',    label: 'Other' },
];

const STATUS_BADGE = {
  submitted: { className: 'badge', text: 'submitted' },
  approved:  { className: 'badge orange', text: 'approved' },
  denied:    { className: 'badge', text: 'denied' },
  reimbursed:{ className: 'badge orange', text: 'reimbursed' },
};

export default function ExpensesPage() {
  const { data: expenses = [], isLoading } = useExpenses();
  const [showForm, setShowForm] = useState(false);

  return (
    <div className="panel">
      <h3 className="panel-title">
        <span><i className="ph ph-receipt" /> My expenses</span>
        <button className="btn-sm primary" onClick={() => setShowForm(v => !v)}>
          {showForm ? 'Cancel' : '+ New expense'}
        </button>
      </h3>
      {showForm && <ExpenseForm onDone={() => setShowForm(false)} />}
      {isLoading ? (
        <div className="loading">Loading…</div>
      ) : expenses.length === 0 ? (
        <div className="loading">No expenses submitted yet.</div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>Date</th>
              <th>Kind</th>
              <th className="num">Amount</th>
              <th>Notes</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {expenses.map(e => <ExpenseRow key={e.id} e={e} />)}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ExpenseRow({ e }) {
  const del = useDeleteExpense();
  const { show } = useError();
  const remove = async () => {
    if (!confirm(`Delete this expense?`)) return;
    try { await del.mutateAsync(e.id); } catch (err) { show(err.message); }
  };
  const badge = STATUS_BADGE[e.status] || { className: 'badge', text: e.status };
  return (
    <tr>
      <td>{new Date(e.date).toLocaleDateString()}</td>
      <td><span className="badge">{e.kind}</span></td>
      <td className="num">{centsToUsd(e.amount_cents)}</td>
      <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {e.miles ? `${e.miles} mi · ` : ''}{e.notes || ''}
      </td>
      <td><span className={badge.className}>{badge.text}</span></td>
      <td style={{ textAlign: 'right' }}>
        {e.status === 'submitted' && (
          <button className="btn-sm" onClick={remove} disabled={del.isPending}>Del</button>
        )}
      </td>
    </tr>
  );
}

function ExpenseForm({ onDone }) {
  const [kind, setKind] = useState('material');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState('');
  const [miles, setMiles] = useState('');
  const [jobId, setJobId] = useState('');
  const [notes, setNotes] = useState('');
  const submit = useSubmitExpense();
  const { data: jobs = [] } = useJobs('active');
  const { show } = useError();

  const onSubmit = async (ev) => {
    ev.preventDefault();
    try {
      const payload = {
        kind,
        date: Date.parse(date + 'T00:00:00Z'),
        job_id: jobId || null,
        notes: notes || null,
      };
      if (kind === 'mileage') {
        payload.miles = Number(miles) || 0;
        if (amount) payload.amount_cents = Math.round(Number(amount) * 100);
      } else {
        payload.amount_cents = Math.round(Number(amount) * 100);
      }
      await submit.mutateAsync(payload);
      onDone();
    } catch (err) { show(err.message); }
  };

  return (
    <form onSubmit={onSubmit} style={{ padding: 16, background: 'var(--surface-container)', marginBottom: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div className="field">
          <label className="field-label" htmlFor="exp-kind">Kind</label>
          <select id="exp-kind" className="field-select" value={kind} onChange={ev => setKind(ev.target.value)}>
            {KINDS.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="field-label" htmlFor="exp-date">Date</label>
          <input id="exp-date" className="field-input" type="date" value={date}
            onChange={ev => setDate(ev.target.value)} required />
        </div>
        {kind === 'mileage' && (
          <div className="field">
            <label className="field-label" htmlFor="exp-miles">Miles</label>
            <input id="exp-miles" className="field-input" type="number" step="0.1" min="0"
              value={miles} onChange={ev => setMiles(ev.target.value)} />
          </div>
        )}
        <div className="field">
          <label className="field-label" htmlFor="exp-amount">Amount ($)</label>
          <input id="exp-amount" className="field-input" type="number" step="0.01" min="0"
            value={amount} onChange={ev => setAmount(ev.target.value)}
            placeholder={kind === 'mileage' ? 'auto from miles' : ''} />
        </div>
      </div>
      <div className="field">
        <label className="field-label" htmlFor="exp-job">Job (optional)</label>
        <select id="exp-job" className="field-select" value={jobId} onChange={ev => setJobId(ev.target.value)}>
          <option value="">— none —</option>
          {jobs.map(j => <option key={j.id} value={j.id}>{j.name}</option>)}
        </select>
      </div>
      <div className="field">
        <label className="field-label" htmlFor="exp-notes">Notes</label>
        <textarea id="exp-notes" className="field-textarea" value={notes}
          onChange={ev => setNotes(ev.target.value)} placeholder="optional" />
      </div>
      <button className="btn-sm primary" type="submit" disabled={submit.isPending}>
        {submit.isPending ? 'Submitting…' : 'Submit'}
      </button>
    </form>
  );
}
